import { useState } from "react";
import {
  useParams,
  useNavigate,
  generatePath,
  Link as RouterLink,
} from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getAdminShopDetail,
  updateShopStatus,
  updateProductStatus,
  resetShopChangeLimit,
  resetProductChangeLimit,
  flagShopForVerification,
  setShopRecommendationVisibility,
  setShopOffersMode,
} from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";

const shopStatusColor = { ACTIVE: "success", SUSPENDED: "warning" };
const productStatusColor = {
  PUBLISHED: "success",
  SUSPENDED: "error",
  ARCHIVED: "default",
  DRAFT: "info",
  OUT_OF_STOCK: "warning",
};

// Mirrors getRateLimitStatus on the backend (utility/utility.js) — shop
// location and phone number aren't simple *LastChangedAt locks, each is capped
// at SHOP_RATE_LIMIT_MAX changes per rolling SHOP_RATE_LIMIT_DAYS window
// (controllers/shopAdmin/shopManagement.js).
const SHOP_RATE_LIMIT_MAX = 5;
const SHOP_RATE_LIMIT_DAYS = 60;

const getShopRateLimitStatus = (changeLog = []) => {
  const windowStart = Date.now() - SHOP_RATE_LIMIT_DAYS * 24 * 60 * 60 * 1000;
  const withinWindow = changeLog
    .map((d) => new Date(d))
    .filter((d) => d.getTime() > windowStart)
    .sort((a, b) => a - b);
  const usedCount = withinWindow.length;
  const isLocked = usedCount >= SHOP_RATE_LIMIT_MAX;
  const nextAvailableAt = isLocked
    ? new Date(
        withinWindow[0].getTime() + SHOP_RATE_LIMIT_DAYS * 24 * 60 * 60 * 1000,
      )
    : null;
  return { usedCount, isLocked, nextAvailableAt };
};

const ShopStatusDialog = ({
  open,
  onClose,
  targetStatus,
  onConfirm,
  loading,
}) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {targetStatus === "SUSPENDED" ? "Suspend Shop" : "Reactivate Shop"}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={targetStatus === "SUSPENDED" ? "error" : "success"}
          disabled={loading}
          onClick={() => {
            onConfirm(reason);
            setReason("");
          }}
        >
          {loading ? <CircularProgress size={18} /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProductStatusDialog = ({
  open,
  productId,
  currentStatus,
  onClose,
  onConfirm,
  loading,
}) => {
  const [targetStatus, setTargetStatus] = useState("PUBLISHED");
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {currentStatus === "SUSPENDED"
          ? "Restore Product"
          : "Admin Delist Product"}
      </DialogTitle>
      <DialogContent>
        {currentStatus === "SUSPENDED" ? (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Restore to</InputLabel>
            <Select
              value={targetStatus}
              label="Restore to"
              onChange={(e) => setTargetStatus(e.target.value)}
            >
              <MenuItem value="PUBLISHED">Published</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" mt={1}>
            This will set the product status to SUSPENDED and hide it from
            public view.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={currentStatus === "SUSPENDED" ? "success" : "error"}
          disabled={loading}
          onClick={() =>
            onConfirm(
              currentStatus === "SUSPENDED" ? targetStatus : "SUSPENDED",
            )
          }
        >
          {loading ? <CircularProgress size={18} /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ShopDetail = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [shopDialog, setShopDialog] = useState(null);
  const [productDialog, setProductDialog] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-shop", shopId],
    queryFn: () => getAdminShopDetail(shopId),
  });

  const shopMutation = useMutation({
    mutationFn: ({ status, reason }) =>
      updateShopStatus(shopId, { status, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-shop", shopId] });
      qc.invalidateQueries({ queryKey: ["admin-shops"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setShopDialog(null);
      setSnackbar({ severity: "success", message: "Shop status updated." });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      }),
  });

  const productMutation = useMutation({
    mutationFn: ({ productId, status }) =>
      updateProductStatus(productId, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-shop", shopId] });
      setProductDialog(null);
      setSnackbar({ severity: "success", message: "Product status updated." });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      }),
  });

  const resetShopLimitMutation = useMutation({
    mutationFn: ({ field }) => resetShopChangeLimit(shopId, { field }),
    onSuccess: (_, { field }) => {
      qc.invalidateQueries({ queryKey: ["admin-shop", shopId] });
      setSnackbar({
        severity: "success",
        message: `Shop ${field} change limit reset.`,
      });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed to reset limit.",
      }),
  });

  const resetProductLimitMutation = useMutation({
    mutationFn: ({ productId, field }) =>
      resetProductChangeLimit(productId, { field }),
    onSuccess: (_, { field }) => {
      qc.invalidateQueries({ queryKey: ["admin-shop", shopId] });
      setSnackbar({
        severity: "success",
        message: `Product ${field} change limit reset.`,
      });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed to reset limit.",
      }),
  });

  const flagVerificationMutation = useMutation({
    mutationFn: ({ required }) => flagShopForVerification(shopId, { required }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-shop", shopId] });
      setSnackbar({
        severity: "success",
        message: "Shop verification flag updated.",
      });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      }),
  });

  const recommendationsMutation = useMutation({
    mutationFn: ({ hidden }) =>
      setShopRecommendationVisibility(shopId, { hidden }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-shop", shopId] });
      setSnackbar({
        severity: "success",
        message: "Recommendation visibility updated.",
      });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      }),
  });

  const offersModeMutation = useMutation({
    mutationFn: ({ revoked }) => setShopOffersMode(shopId, { revoked }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["admin-shop", shopId] });
      setSnackbar({
        severity: "success",
        message: result.offersModeRevoked
          ? `Hidden prices turned off. ${result.archivedCount} product(s) archived.`
          : "Hidden prices allowed again.",
      });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      }),
  });

  if (isLoading) return <CircularProgress />;
  if (error || !data) return <Alert severity="error">Shop not found.</Alert>;

  const { shop, products, reports } = data;
  const currentStatus = shop.status || "ACTIVE";
  const owner = shop.createdBy;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(frontendRoutes.ADMIN_SHOPS)}
        sx={{ mb: 2 }}
      >
        Back to Shops
      </Button>
      <Typography variant="h5" fontWeight={700} mb={3}>
        {shop.shopName}
      </Typography>

      <Stack
        spacing={2}
        direction={{ xs: "column", md: "row" }}
        alignItems="flex-start"
        mb={2}
      >
        {/* Shop info */}
        <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
          <CardContent>
            <Typography fontWeight={600} mb={1}>
              Shop Info
            </Typography>
            <Typography variant="body2">{shop.description}</Typography>
            {shop.orderInstructions && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1}
                >
                  Order Instructions
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {shop.orderInstructions}
                </Typography>
              </>
            )}
            {shop.deliveryInstructions && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1}
                >
                  Delivery Instructions
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {shop.deliveryInstructions}
                </Typography>
              </>
            )}
            <Typography variant="body2" mt={1}>
              Country: {shop.country || "—"}
            </Typography>
            <Typography variant="body2">
              Rating: {shop.rating?.toFixed(1) || "—"} ({shop.numReviews || 0}{" "}
              reviews)
            </Typography>
            <Typography variant="body2">
              Created: {new Date(shop.createdAt).toLocaleDateString()}
            </Typography>
            <Typography
              variant="body2"
              color={
                shop.disputesLostCount > 0 ? "warning.dark" : "text.primary"
              }
            >
              Disputes lost: {shop.disputesLostCount || 0}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              title="Accepted orders called off by agreement. A cancelled order can't be reviewed, so a high count can mean buyers are being steered to cancel."
            >
              Orders called off by agreement:{" "}
              {shop.mutualCancellationCount || 0}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={currentStatus}
                color={shopStatusColor[currentStatus]}
                size="small"
              />
              {shop.isIdentityVerified && (
                <Chip label="Verified" color="success" size="small" />
              )}
              {shop.identityVerificationRequired && (
                <Chip
                  label="Verification required"
                  color="warning"
                  size="small"
                />
              )}
              {shop.hiddenFromRecommendations && (
                <Chip label="Hidden from recommendations" size="small" />
              )}
              {shop.offersModeRevoked && (
                <Chip
                  label="Hidden prices revoked"
                  color="warning"
                  size="small"
                />
              )}
            </Stack>
            {shop.statusReason && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
              >
                Reason: {shop.statusReason}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Owner + actions */}
        <Card variant="outlined" sx={{ minWidth: 220 }}>
          <CardContent>
            <Typography fontWeight={600} mb={1}>
              Owner
            </Typography>
            {owner ? (
              <>
                <Typography variant="body2">{owner.name}</Typography>
                <Typography variant="body2">
                  {owner.email || owner.phoneNumber || "—"}
                </Typography>
                <Button
                  size="small"
                  sx={{ mt: 0.5 }}
                  onClick={() =>
                    navigate(
                      generatePath(frontendRoutes.ADMIN_USER_DETAIL, {
                        userId: owner._id || owner.id,
                      }),
                    )
                  }
                >
                  View User
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
            <Typography fontWeight={600} mt={2} mb={1}>
              Shop Actions
            </Typography>
            <Stack spacing={1} alignItems="flex-start">
              {currentStatus === "ACTIVE" ? (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShopDialog("SUSPENDED")}
                >
                  Suspend Shop
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => setShopDialog("ACTIVE")}
                >
                  Reactivate Shop
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                disabled={flagVerificationMutation.isPending}
                onClick={() =>
                  flagVerificationMutation.mutate({
                    required: !shop.identityVerificationRequired,
                  })
                }
              >
                {shop.identityVerificationRequired
                  ? "Clear Verification Flag"
                  : "Flag for Verification"}
              </Button>
              <Button
                variant="outlined"
                disabled={recommendationsMutation.isPending}
                onClick={() =>
                  recommendationsMutation.mutate({
                    hidden: !shop.hiddenFromRecommendations,
                  })
                }
              >
                {shop.hiddenFromRecommendations
                  ? "Show in Recommendations"
                  : "Hide from Recommendations"}
              </Button>
              <Button
                variant="outlined"
                color={shop.offersModeRevoked ? "primary" : "warning"}
                disabled={offersModeMutation.isPending}
                onClick={() => {
                  // Revoking archives the shop's live hidden price products.
                  if (
                    !shop.offersModeRevoked &&
                    !window.confirm(
                      "Turn off hidden prices for this shop? Its hidden price products will be archived and the seller notified.",
                    )
                  ) {
                    return;
                  }
                  offersModeMutation.mutate({
                    revoked: !shop.offersModeRevoked,
                  });
                }}
              >
                {shop.offersModeRevoked
                  ? "Allow Hidden Prices"
                  : "Revoke Hidden Prices"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Shop Change Limits */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={0.5}>
            Shop Change Limits
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Reset a field lock so the shop owner can update it immediately.
          </Typography>
          {[
            {
              label: "Location",
              field: "location",
              changeLog: shop.locationChangeLog,
            },
            {
              // Also sets the shop's country and currency.
              label: "Phone number",
              field: "phone",
              changeLog: shop.phoneChangeLog,
            },
          ].map(({ label, field, changeLog }) => {
            const { usedCount, isLocked, nextAvailableAt } =
              getShopRateLimitStatus(changeLog);
            return (
              <Stack
                key={field}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={0.75}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={isLocked ? "warning.main" : "text.secondary"}
                  >
                    {isLocked
                      ? `${usedCount}/${SHOP_RATE_LIMIT_MAX} changes used. Next available ${nextAvailableAt.toLocaleDateString()}`
                      : `${usedCount}/${SHOP_RATE_LIMIT_MAX} changes used in the last ${SHOP_RATE_LIMIT_DAYS} days`}
                  </Typography>
                </Box>
                {isLocked && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={resetShopLimitMutation.isPending}
                    onClick={() => resetShopLimitMutation.mutate({ field })}
                  >
                    Reset
                  </Button>
                )}
              </Stack>
            );
          })}
          {[
            {
              label: "Shop Name",
              field: "name",
              lastChangedAt: shop.nameLastChangedAt,
              days: 90,
            },
            {
              label: "Categories",
              field: "categories",
              lastChangedAt: shop.categoryLastChangedAt,
              days: 60,
            },
            {
              label: "Bank Transfer",
              field: "paymentDetails:BANK_TRANSFER",
              lastChangedAt: shop.paymentDetailLastChangedAt?.BANK_TRANSFER,
              days: 30,
            },
            {
              label: "Venmo",
              field: "paymentDetails:VENMO",
              lastChangedAt: shop.paymentDetailLastChangedAt?.VENMO,
              days: 30,
            },
            {
              label: "Zelle",
              field: "paymentDetails:ZELLE",
              lastChangedAt: shop.paymentDetailLastChangedAt?.ZELLE,
              days: 30,
            },
            {
              label: "Mobile Money",
              field: "paymentDetails:MOBILE_MONEY",
              lastChangedAt: shop.paymentDetailLastChangedAt?.MOBILE_MONEY,
              days: 30,
            },
          ].map(({ label, field, lastChangedAt, days }) => {
            const nextDate = lastChangedAt
              ? new Date(
                  new Date(lastChangedAt).getTime() +
                    days * 24 * 60 * 60 * 1000,
                )
              : null;
            const isLocked = nextDate && nextDate > new Date();
            return (
              <Stack
                key={field}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={0.75}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {label}
                  </Typography>
                  {lastChangedAt ? (
                    <Typography
                      variant="caption"
                      color={isLocked ? "warning.main" : "text.secondary"}
                    >
                      {isLocked
                        ? `Locked until ${nextDate.toLocaleDateString()}`
                        : `Last changed ${new Date(lastChangedAt).toLocaleDateString()}`}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Never changed
                    </Typography>
                  )}
                </Box>
                {isLocked && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={resetShopLimitMutation.isPending}
                    onClick={() => resetShopLimitMutation.mutate({ field })}
                  >
                    Reset
                  </Button>
                )}
              </Stack>
            );
          })}
        </CardContent>
      </Card>

      {/* Products */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={1}>
            Products (recent 20)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sold</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products?.map((p) => {
                const nameLocked =
                  p.nameLastChangedAt &&
                  new Date(
                    new Date(p.nameLastChangedAt).getTime() +
                      60 * 24 * 60 * 60 * 1000,
                  ) > new Date();
                const catLocked =
                  p.categoryLastChangedAt &&
                  new Date(
                    new Date(p.categoryLastChangedAt).getTime() +
                      30 * 24 * 60 * 60 * 1000,
                  ) > new Date();
                return (
                  <TableRow key={p._id}>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={generatePath(frontendRoutes.ADMIN_PRODUCT_DETAIL, {
                          productId: p._id,
                        })}
                        underline="hover"
                      >
                        {p.productName}
                      </Link>
                      {(nameLocked || catLocked) && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          display="block"
                        >
                          {[nameLocked && "name", catLocked && "categories"]
                            .filter(Boolean)
                            .join(", ")}{" "}
                          locked
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.status}
                        color={productStatusColor[p.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{p.numSold || 0}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {(p.status === "PUBLISHED" ||
                          p.status === "OUT_OF_STOCK") && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => setProductDialog(p)}
                          >
                            Delist
                          </Button>
                        )}
                        {p.status === "SUSPENDED" && (
                          <Button
                            size="small"
                            color="success"
                            onClick={() => setProductDialog(p)}
                          >
                            Restore
                          </Button>
                        )}
                        {nameLocked && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={resetProductLimitMutation.isPending}
                            onClick={() =>
                              resetProductLimitMutation.mutate({
                                productId: p._id,
                                field: "name",
                              })
                            }
                          >
                            Reset name
                          </Button>
                        )}
                        {catLocked && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={resetProductLimitMutation.isPending}
                            onClick={() =>
                              resetProductLimitMutation.mutate({
                                productId: p._id,
                                field: "categories",
                              })
                            }
                          >
                            Reset cats
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reports */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={1}>
            Reports Against This Shop ({reports?.length || 0})
          </Typography>
          {reports?.length ? (
            reports.map((r) => (
              <Box key={r._id} py={0.5}>
                <Typography variant="body2">
                  {r.reason?.replace(/_/g, " ")} —{" "}
                  <Chip label={r.status} size="small" /> —{" "}
                  {new Date(r.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reports.
            </Typography>
          )}
        </CardContent>
      </Card>

      <ShopStatusDialog
        open={!!shopDialog}
        targetStatus={shopDialog}
        onClose={() => setShopDialog(null)}
        loading={shopMutation.isPending}
        onConfirm={(reason) =>
          shopMutation.mutate({ status: shopDialog, reason })
        }
      />

      {productDialog && (
        <ProductStatusDialog
          open={!!productDialog}
          productId={productDialog._id}
          currentStatus={productDialog.status}
          onClose={() => setProductDialog(null)}
          loading={productMutation.isPending}
          onConfirm={(status) =>
            productMutation.mutate({ productId: productDialog._id, status })
          }
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShopDetail;
